import { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Center, HStack, Icon, Menu, MenuButton, MenuItem, MenuList, Portal, Spinner, VStack,
} from "@chakra-ui/react";
import CDS from "design-system";
import { FiChevronDown, FiUsers } from "react-icons/fi";
import AppPage from "layouts/AppPage";
import { useNavbar } from "hooks/useNavbar";
import useCaminoStore from "hooks/useCaminoStore";
import SendJourneyRemindersById from "features/SendMessage/Components/SendJourneyRemindersById";
import {
  useAutoAddPrefs, useDensityMode, useNow, useSortMode, useTemplateSortMode,
  useTaskGridPreferencesQuery, useTaskGridQuery, useUpdateTaskGridPreferencesMutation,
} from "../hooks";
import TaskGrid from "../Components/TaskGrid";
import QuickFilters from "../Components/QuickFilters";
import GridSettingsPopover from "../Components/GridSettingsPopover";
import DensityToggle from "../Components/DensityToggle";
import SortMenu from "../Components/SortMenu";
import TemplateSortMenu from "../Components/TemplateSortMenu";
import type { AutoAddSide, TaskGridPreferences } from "../types";
import { matchesFilter, type QuickFilter } from "../helpers";

const ROLE_DISPLAY: Record<string, string> = {
  new_hire: "New hire",
  manager: "Manager",
  buddy: "Buddy",
  people_team_contact: "People Team",
  recruiter: "Recruiter",
  it_team_contact: "IT",
};

const EMPTY_PREFERENCES: TaskGridPreferences = {
  hiddenJourneyIds: [],
  hiddenTemplateIds: [],
  journeyOrder: [],
  templateOrder: [],
  density: "comfortable",
  sort: "start_date_asc",
  templateSort: "custom",
  autoAdd: true,
  autoAddSide: "left",
};

export default function TaskGridPage() {
  const { setBackLocationPath, setBackLocationName, setPageTitle } = useNavbar();
  const currentUserId = useCaminoStore((s) => s.currentUser?.id ?? null);
  const grid = useTaskGridQuery();
  const prefsQuery = useTaskGridPreferencesQuery();
  const updatePrefs = useUpdateTaskGridPreferencesMutation();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>("all");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const { autoAdd, setAutoAdd, side: autoAddSide, setSide: setAutoAddSide } = useAutoAddPrefs();
  const newHirePlacement: AutoAddSide = autoAdd ? autoAddSide : "right";
  const { density, setDensity } = useDensityMode();
  const { sort, setSort } = useSortMode();
  const { templateSort, setTemplateSort } = useTemplateSortMode();

  useEffect(() => {
    setPageTitle("Onboarding tasks");
    return () => {
      setPageTitle(null);
      setBackLocationName(null);
      setBackLocationPath(null);
    };
  }, [setBackLocationName, setBackLocationPath, setPageTitle]);

  // Persist newly-appeared journeys into journey_order so the placement is
  // stable on future loads. A journey is "new" when it's visible (not in
  // hidden_journey_ids) but missing from journey_order — covers brand-new
  // kickoffs and legacy users with empty prefs. Skipped entirely when the
  // user has turned auto-add off.
  useEffect(() => {
    if (!grid.data || !prefsQuery.data) return;
    if (!autoAdd) return;
    if (updatePrefs.isPending) return;

    const hiddenSet = new Set(prefsQuery.data.hiddenJourneyIds.map(String));
    const orderSet = new Set(prefsQuery.data.journeyOrder.map(String));
    const newIds = grid.data.newHireJourneys
      .filter((j) => !hiddenSet.has(String(j.id)) && !orderSet.has(String(j.id)))
      .map((j) => String(j.id));

    if (newIds.length === 0) return;

    const existing = prefsQuery.data.journeyOrder.map(String);
    const nextOrder = autoAddSide === "left" ? [...newIds, ...existing] : [...existing, ...newIds];
    updatePrefs.mutate({ journeyOrder: nextOrder });
  }, [grid.data, prefsQuery.data, autoAdd, autoAddSide, updatePrefs]);

  const today = useNow();

  const counts = useMemo(() => {
    const tasks = grid.data?.tasks ?? [];
    return {
      all: tasks.length,
      open: tasks.filter((t) => matchesFilter(t, "open", currentUserId, today)).length,
      overdue: tasks.filter((t) => matchesFilter(t, "overdue", currentUserId, today)).length,
      due_within_3_days: tasks.filter((t) => matchesFilter(t, "due_within_3_days", currentUserId, today)).length,
      mine: tasks.filter((t) => matchesFilter(t, "mine", currentUserId, today)).length,
    };
  }, [grid.data?.tasks, currentUserId, today]);

  // Pull every distinct assignee_role we see across tasks so the role filter
  // surfaces the same roles that templates in this view actually use.
  const availableRoles = useMemo(() => {
    const tasks = grid.data?.tasks ?? [];
    const set = new Set<string>();
    tasks.forEach((t) => {
      if (t.assigneeRole) set.add(t.assigneeRole);
    });
    return Array.from(set).sort();
  }, [grid.data?.tasks]);

  if (grid.isLoading || prefsQuery.isLoading) {
    return (
      <AppPage>
        <Center py={20}><Spinner /></Center>
      </AppPage>
    );
  }

  if (grid.isError || !grid.data) {
    return (
      <AppPage>
        <Center py={20}>
          <CDS.Text12 color="red.500">Failed to load the task grid.</CDS.Text12>
        </Center>
      </AppPage>
    );
  }

  const preferences = prefsQuery.data ?? EMPTY_PREFERENCES;
  const { newHireJourneys, taskTemplates, templatesWithTasksIds, tasks, assignees } = grid.data;

  // Normalize ids to strings — prefs come back from the server as integers,
  // blueprint ids are prefix-id strings. Without normalizing, the Hidden pill
  // counts 0 even when items are actually hidden, so it never stays visible.
  const hiddenJourneySet = new Set(preferences.hiddenJourneyIds.map(String));
  const hiddenJourneys = newHireJourneys.filter((j) => hiddenJourneySet.has(String(j.id)));

  const setHidden = (
    key: "hiddenJourneyIds" | "hiddenTemplateIds",
    ids: number[],
  ) => {
    updatePrefs.mutate({ [key]: Array.from(new Set(ids)) });
  };

  const hideJourney = (id: number) => setHidden("hiddenJourneyIds", [...preferences.hiddenJourneyIds, id]);
  const restoreJourney = (id: number) => setHidden(
    "hiddenJourneyIds",
    preferences.hiddenJourneyIds.filter((x) => String(x) !== String(id)),
  );

  const hideTemplate = (id: number) => setHidden("hiddenTemplateIds", [...preferences.hiddenTemplateIds, id]);
  // Restore = unhide AND mark as explicitly added. templateOrder is the source
  // of truth for visibility, so a template that was never hidden — just never
  // added — needs the id appended for the row to appear in the grid.
  const restoreTemplate = (id: number) => {
    const orderIds = preferences.templateOrder.map(String);
    const alreadyInOrder = orderIds.includes(String(id));
    updatePrefs.mutate({
      hiddenTemplateIds: preferences.hiddenTemplateIds.filter((x) => String(x) !== String(id)),
      ...(alreadyInOrder ? {} : { templateOrder: [...preferences.templateOrder, id] }),
    });
  };
  // PreferenceIds (number[]) is the persisted shape; the patch type accepts
  // either string[] or number[] and api.ts coerces at the boundary.
  const reorderJourneys = (orderedIds: string[]) => updatePrefs.mutate({ journeyOrder: orderedIds });
  const reorderTemplates = (orderedIds: string[]) => updatePrefs.mutate({ templateOrder: orderedIds });

  // "Add all active journeys" — bring every active journey into the view in one shot.
  // Unhides anything in hidden_journey_ids and appends/prepends anything missing
  // from journey_order so nothing is left dangling.
  const orderSetForAddAll = new Set(preferences.journeyOrder.map(String));
  const journeysNotInOrder = newHireJourneys
    .map((j) => String(j.id))
    .filter((id) => !orderSetForAddAll.has(id));
  const journeysNotInView = hiddenJourneys.length + journeysNotInOrder.length;

  const showAllJourneys = () => {
    const existing = preferences.journeyOrder.map(String);
    const nextOrder = newHirePlacement === "left"
      ? [...journeysNotInOrder, ...existing]
      : [...existing, ...journeysNotInOrder];
    updatePrefs.mutate({
      hiddenJourneyIds: [],
      journeyOrder: nextOrder,
    });
  };

  // Mirror logic for templates: hidden + missing from template_order. We only
  // count templates that actually have tasks (a row needs cells to render).
  const templateOrderSet = new Set(preferences.templateOrder.map(String));
  const templatesWithTasksSet = new Set(templatesWithTasksIds.map(String));
  const hiddenTemplateSet = new Set(preferences.hiddenTemplateIds.map(String));
  const hiddenTemplatesWithTasks = taskTemplates.filter(
    (t) => hiddenTemplateSet.has(String(t.id)) && templatesWithTasksSet.has(String(t.id)),
  );
  const templatesNotInOrder = taskTemplates
    .filter((t) => templatesWithTasksSet.has(String(t.id)) && !templateOrderSet.has(String(t.id)))
    .map((t) => String(t.id));
  const templatesNotInView = hiddenTemplatesWithTasks.length + templatesNotInOrder.length;

  const showAllTemplates = () => {
    const existing = preferences.templateOrder.map(String);
    const nextOrder = [...existing, ...templatesNotInOrder];
    updatePrefs.mutate({
      hiddenTemplateIds: [],
      templateOrder: nextOrder,
    });
  };

  return (
    <AppPage>
      <VStack spacing={4} alignItems="stretch">
        <HStack justify="space-between" align="center" wrap="wrap" gap={2}>
          <HStack spacing={3} align="center">
            <CDS.Text18 fontWeight="semibold">Onboarding tasks</CDS.Text18>
            {/* Reserved slot for the eventual Board / Queue / Roster view switcher.
                Renders nothing for now so a future PR doesn't reflow the toolbar. */}
            <Box id="view-switch-slot" />
          </HStack>
          <HStack spacing={2} align="center">
            <DensityToggle density={density} onChange={setDensity} />
            <GridSettingsPopover
              autoAdd={autoAdd}
              onAutoAddChange={setAutoAdd}
              side={autoAddSide}
              onSideChange={setAutoAddSide}
              journeysNotInViewCount={journeysNotInView}
              onAddAllJourneys={showAllJourneys}
              templatesNotInViewCount={templatesNotInView}
              onAddAllTemplates={showAllTemplates}
            />
          </HStack>
        </HStack>

        <HStack justify="space-between" wrap="wrap" gap={2}>
          <HStack spacing={2} wrap="wrap">
            <QuickFilters value={quickFilter} onChange={setQuickFilter} counts={counts} />
          </HStack>
          <HStack spacing={2}>
            <SortMenu sort={sort} onChange={setSort} />
            <TemplateSortMenu sort={templateSort} onChange={setTemplateSort} />
            {availableRoles.length > 0 && (
            <Menu placement="bottom-end">
              <MenuButton
                as={Button}
                size="sm"
                variant="outline"
                colorScheme="gray"
                h="28px"
                minH="28px"
                px={2.5}
                fontSize="13px"
                fontWeight="normal"
                leftIcon={<Icon as={FiUsers} boxSize={3.5} />}
                rightIcon={<Icon as={FiChevronDown} boxSize={3.5} />}
              >
                {roleFilter === "all" ? "All roles" : ROLE_DISPLAY[roleFilter] ?? roleFilter}
              </MenuButton>
              <Portal>
                <MenuList>
                  <MenuItem onClick={() => setRoleFilter("all")}>All roles</MenuItem>
                  {availableRoles.map((r) => (
                    <MenuItem key={r} onClick={() => setRoleFilter(r)}>
                      {ROLE_DISPLAY[r] ?? r}
                    </MenuItem>
                  ))}
                </MenuList>
              </Portal>
            </Menu>
            )}
          </HStack>
        </HStack>

        <Box>
          <TaskGrid
            journeys={newHireJourneys}
            templates={taskTemplates}
            tasks={tasks}
            assignees={assignees}
            preferences={preferences}
            quickFilter={quickFilter}
            roleFilter={roleFilter}
            currentUserId={currentUserId}
            newHirePlacement={newHirePlacement}
            density={density}
            sort={sort}
            onSortChange={setSort}
            templateSort={templateSort}
            onTemplateSortChange={setTemplateSort}
            onHideJourney={hideJourney}
            onHideTemplate={hideTemplate}
            onRestoreJourney={restoreJourney}
            onRestoreTemplate={restoreTemplate}
            onReorderJourneys={reorderJourneys}
            onReorderTemplates={reorderTemplates}
          />
        </Box>
      </VStack>

      {/* Mounted once at the page level: any descendant opens the Send
          Reminders modal by setting `?remindersJourneyId=` in the URL — keeps
          the stakeholder-data fetch off the per-column render path. */}
      <SendJourneyRemindersById />
    </AppPage>
  );
}
